import React, { useEffect, useRef, useState } from "react";
import {
	CalendarIcon,
	MagnifyingGlassIcon,
	XMarkIcon,
	EyeIcon,
} from "@heroicons/react/24/outline";
import { BiTrip } from "react-icons/bi";
import AssignModal from "../Seat/AssignModal";
import { useBusStore, buildBusSeatStatus } from "../../../store/busStore";
import { useEventStore } from "../../../store/eventStore";

const buildEventMap = (events) => {
	const map = {};
	(events || []).forEach((event) => {
		const key = `${event.ev_ym}-${event.ev_seq}`;
		map[key] = event;
	});
	return map;
};

const formatDate = (value) => {
	if (!value) return "-";
	if (value.includes("-")) return value;
	if (value.length === 8) {
		return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6)}`;
	}
	return value;
};

export default function BusConfirmed() {
	const { listServices, getSeats, getSeatmap } = useBusStore();
	const { getEventsByDate, getReservationInfo } = useEventStore();

	const [services, setServices] = useState([]);
	const [eventMap, setEventMap] = useState({});
	const [searchDate, setSearchDate] = useState("");
	const [busCode, setBusCode] = useState("");
	const [evYm, setEvYm] = useState("");
	const [evSeq, setEvSeq] = useState("");
	const [localLoading, setLocalLoading] = useState(false);
	const [error, setError] = useState(null);
	const [loadingBusCode, setLoadingBusCode] = useState(null);

	const [isViewModalOpen, setIsViewModalOpen] = useState(false);
	const [viewSeatData, setViewSeatData] = useState(null);
	const [viewSeatLayout, setViewSeatLayout] = useState(null);
	const [viewAssignmentData, setViewAssignmentData] = useState(null);
	const initialLoadRef = useRef(true);

	useEffect(() => {
		const today = new Date().toISOString().split("T")[0];
		setSearchDate(today);
	}, []);

	useEffect(() => {
		if (initialLoadRef.current && searchDate) {
			handleSearch();
			initialLoadRef.current = false;
		}
	}, [searchDate]);

	const handleClearDate = () => {
		setSearchDate("");
	};

	const handleSearch = async () => {
		setLocalLoading(true);
		setError(null);

		const params = {};
		const formattedDate = searchDate ? searchDate.replace(/-/g, "") : "";
		if (formattedDate) {
			params.service_date = formattedDate;
		}
		if (busCode.trim()) {
			params.bus_code = busCode.trim();
		}
		if (evYm.trim()) {
			params.ev_ym = evYm.trim();
		}
		const evSeqValue = parseInt(evSeq, 10);
		if (Number.isInteger(evSeqValue)) {
			params.ev_seq = evSeqValue;
		}

		try {
			const [serviceResponse, eventResponse] = await Promise.all([
				listServices(params),
				formattedDate
					? getEventsByDate(formattedDate, formattedDate)
					: Promise.resolve({ success: false, data: [] }),
			]);

			if (serviceResponse.success) {
				const confirmed = (serviceResponse.data || []).filter(
					(service) => service.is_assigned
				);
				setServices(confirmed);
			} else {
				setServices([]);
				setError(serviceResponse.error || "버스 좌석 등록 조회 실패");
			}

			if (eventResponse.success) {
				setEventMap(buildEventMap(eventResponse.data || []));
			} else {
				setEventMap({});
			}
		} catch (err) {
			console.error("버스 좌석 등록 조회 실패:", err);
			setError("버스 좌석 등록 조회 중 오류가 발생했습니다.");
			setServices([]);
			setEventMap({});
		} finally {
			setLocalLoading(false);
		}
	};

	const handleViewSeatmap = async (service) => {
		if (!service?.bus_code) return;

		setLoadingBusCode(service.bus_code);
		try {
			const [seatsResponse, reservationResponse, seatmapResponse] =
				await Promise.all([
					getSeats(service.bus_code),
					getReservationInfo(service.ev_ym, service.ev_seq, "go"),
					getSeatmap(service.bus_code, service.seatmap_layout_id, null),
				]);

			const reservations = reservationResponse.success
				? reservationResponse.data
				: [];
			const seatmapLayout = seatmapResponse.success
				? seatmapResponse.seatmapLayout
				: null;
			const seatStatus = seatsResponse.success
				? buildBusSeatStatus(seatsResponse.data?.items || [], reservations, service.car_number, seatmapLayout)
				: {};

			const eventKey = `${service.ev_ym}-${service.ev_seq}`;
			const eventName = eventMap[eventKey]?.ev_nm || "";
			const eventData = {
				ev_ym: service.ev_ym,
				ev_seq: service.ev_seq,
				ev_nm: eventName,
				start_day: service.service_date,
			};

			setViewSeatData(seatStatus);
			setViewSeatLayout(seatmapLayout);
			setViewAssignmentData({
				eventData,
				busService: service,
				seatmapLayout,
			});
			setIsViewModalOpen(true);
		} catch (err) {
			console.error("버스 좌석 정보 조회 실패:", err);
			alert("버스 좌석 정보를 불러오지 못했습니다.");
		} finally {
			setLoadingBusCode(null);
		}
	};

	const resolveEventLabel = (service) => {
		const key = `${service.ev_ym}-${service.ev_seq}`;
		return eventMap[key]?.ev_nm || key;
	};

	const DataBox = (
		<div className="overflow-x-auto">
			<table className="min-w-full divide-y divide-gray-200">
				<thead className="bg-gray-50">
					<tr>
						<th className="pl-6 pr-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
							번호
						</th>
						<th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
							버스 코드
						</th>
						<th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
							행사
						</th>
						<th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
							운행일
						</th>
						<th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
							좌석수
						</th>
						<th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
							배정/잔여
						</th>
						<th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
							좌석보기
						</th>
					</tr>
				</thead>
				<tbody className="bg-white divide-y divide-gray-200">
					{services.length > 0 ? (
						services.map((service, index) => (
							<tr key={service.bus_code}>
								<td className="pl-6 pr-3 py-3 whitespace-nowrap text-sm text-gray-500">
									{index + 1}
								</td>
								<td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
									{service.bus_code}
								</td>
								<td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
									{resolveEventLabel(service)}
								</td>
								<td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
									{formatDate(service.service_date)}
								</td>
								<td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
									{service.total_seats || service.seat_count || 0}
								</td>
								<td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
									{service.assigned_seats || 0} / {service.available_seats || 0}
								</td>
								<td className="px-3 py-3 whitespace-nowrap text-right">
									<button
										onClick={() => handleViewSeatmap(service)}
										disabled={loadingBusCode === service.bus_code}
										className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
									>
										{loadingBusCode === service.bus_code ? (
											"불러오는 중..."
										) : (
											<>
												<EyeIcon className="h-4 w-4" />
												보기
											</>
										)}
									</button>
								</td>
							</tr>
						))
					) : (
						<tr>
							<td colSpan="7" className="px-3 py-8 text-center text-sm text-gray-500">
								{error ? (
									<div className="text-red-500">
										<p>오류가 발생했습니다.</p>
										<p>{error}</p>
									</div>
								) : (
									<div className="flex flex-col items-center">
										<BiTrip className="h-10 w-10 text-gray-400 mb-2" />
										<p>확정된 버스 좌석 등록이 없습니다.</p>
										<p className="text-sm">조건을 입력하고 검색해 주세요.</p>
									</div>
								)}
							</td>
						</tr>
					)}
				</tbody>
			</table>
		</div>
	);

	return (
		<div className="py-6">
			<h1 className="text-2xl font-bold text-gray-900 mb-6">
				버스별 배치확정 리스트
			</h1>
			<p className="text-gray-600 mb-4">
				배치가 확정된 버스 좌석 정보를 확인합니다.
			</p>

			<div className="bg-white shadow rounded-lg p-6 mb-6">
				<div className="flex flex-wrap items-end gap-4">
					<div className="relative flex items-center">
						<label htmlFor="searchDate" className="mr-2 text-sm font-medium text-gray-700">
							운행일자
						</label>
						<div className="relative">
							<input
								type="date"
								id="searchDate"
								value={searchDate}
								onChange={(e) => setSearchDate(e.target.value)}
								className="pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
							/>
							<CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
							{searchDate && (
								<button
									onClick={handleClearDate}
									className="absolute right-2 top-1/2 -translate-y-1/2"
								>
									<XMarkIcon className="h-4 w-4 text-gray-400" />
								</button>
							)}
						</div>
					</div>

					<div>
						<label htmlFor="busCode" className="block text-sm font-medium text-gray-700">
							버스 코드
						</label>
						<input
							id="busCode"
							value={busCode}
							onChange={(e) => setBusCode(e.target.value)}
							className="mt-1 w-40 border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
							placeholder="BUS-001"
						/>
					</div>

					<div>
						<label className="block text-sm font-medium text-gray-700">행사 코드</label>
						<div className="mt-1 flex gap-2">
							<input
								value={evYm}
								onChange={(e) => setEvYm(e.target.value)}
								className="w-28 border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
								placeholder="YYYYMM"
							/>
							<input
								value={evSeq}
								onChange={(e) => setEvSeq(e.target.value)}
								className="w-20 border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
								placeholder="SEQ"
							/>
						</div>
					</div>

					<button
						onClick={handleSearch}
						disabled={localLoading}
						className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md text-sm shadow-sm hover:shadow-md transition disabled:opacity-50"
					>
						<div className="flex items-center">
							{localLoading ? (
								<>검색 중...</>
							) : (
								<>
									<MagnifyingGlassIcon className="h-4 w-4 mr-1" />
									검색하기
								</>
							)}
						</div>
					</button>
				</div>
			</div>

			<div className="bg-white shadow rounded-lg overflow-hidden">
				{localLoading ? (
					<div className="flex justify-center items-center p-12">
						<div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
					</div>
				) : (
					DataBox
				)}
			</div>

			<AssignModal
				isOpen={isViewModalOpen}
				onClose={() => {
					setIsViewModalOpen(false);
					setViewSeatData(null);
					setViewSeatLayout(null);
					setViewAssignmentData(null);
				}}
				readOnly
				assignmentData={viewAssignmentData}
				initialSeatData={viewSeatData}
				initialSeatLayout={viewSeatLayout}
				onEditConfirm={() => {
					setIsViewModalOpen(false);
					handleSearch();
				}}
			/>
		</div>
	);
}
